import type { QueryClient } from '@tanstack/react-query';

import type { CalendarEvent, EventDetail } from './types';

// Abort in-flight event fetches before writing optimistically — otherwise a
// poll response that was already on the wire lands after the optimistic write
// and snaps the UI back until the mutation settles.
function cancelEventQueries(qc: QueryClient): void {
  void qc.cancelQueries({ queryKey: ['events'] });
  void qc.cancelQueries({ queryKey: ['agenda'] });
}

// Invalidate every query that renders events. Call from a mutation's
// onSettled so optimistic entries (which carry fabricated ids) are replaced
// by server truth everywhere, including per-calendar agenda panes.
export function invalidateEventQueries(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['events'] });
  qc.invalidateQueries({ queryKey: ['agenda'] });
}

export function optimisticAddEvent(qc: QueryClient, ev: CalendarEvent): void {
  cancelEventQueries(qc);
  qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
    old ? [...old, ev] : old
  );
  // Agenda queries are per-calendar (['agenda', calendarId, ...]); only the
  // matching calendar's agenda should gain the event.
  qc.setQueriesData<CalendarEvent[]>({ queryKey: ['agenda', ev.calendar_id] }, (old) =>
    old ? [...old, ev] : old
  );
}

export function optimisticUpdateEvent(
  qc: QueryClient,
  id: string,
  listPatch: Partial<CalendarEvent>,
  detailPatch?: Partial<EventDetail>
): void {
  cancelEventQueries(qc);
  qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
    old ? old.map((e) => (e.id === id ? { ...e, ...listPatch } : e)) : old
  );
  qc.setQueriesData<CalendarEvent[]>({ queryKey: ['agenda'] }, (old) =>
    old ? old.map((e) => (e.id === id ? { ...e, ...listPatch } : e)) : old
  );
  qc.setQueryData<EventDetail>(['event', id], (old) =>
    old ? { ...old, ...listPatch, ...detailPatch } : old
  );
}

export function optimisticRemoveEvent(qc: QueryClient, id: string): void {
  cancelEventQueries(qc);
  qc.setQueriesData<CalendarEvent[]>({ queryKey: ['events'] }, (old) =>
    old ? old.filter((e) => e.id !== id) : old
  );
  qc.setQueriesData<CalendarEvent[]>({ queryKey: ['agenda'] }, (old) =>
    old ? old.filter((e) => e.id !== id) : old
  );
}
